import { EventVenue } from '@shared/utils/eventPlace';
import { ButtonVariant } from '@withjoy/joykit/components/ButtonV2/Button.types';
import type { HotelTileCardProps } from '@shared/components/HotelTileCard';
import { AccommodationCombined, AccommodationCombinedType } from '../Accommodations/Accommodation.types';
import { getValidCoordinates } from '@shared/core/places/location';

interface AdapterConfig
  extends Readonly<{
    idx: number;
    accommodation: AccommodationCombined;
    reservationCount?: number;
    carouselImages?: ReadonlyArray<{ src: string; alt: string }>;
    locationInfo?: EventVenue;
    ctaTitle: string;
    ctaUrl?: string;
    ctaVariant?: ButtonVariant;
    isLinkEnabled: boolean;
    shouldShowButton: boolean;
    shouldShowPrice: boolean;
    enableHoverShadow?: boolean;
    enableHorizontalView?: boolean;
    eventPhoto?: string | null;
    eventDisplayName?: string;
    onSelectAccommodation?: React.MouseEventHandler;
    setLocationBeingHovered?: (id: string | undefined) => void;
  }> {}

/**
 * Adapter function to convert AccommodationCombined to HotelTileCardProps
 * Maps the complex accommodation data structure to the generic HotelTileCard props
 */
export const accommodationToHotelTileProps = (config: AdapterConfig): HotelTileCardProps => {
  const {
    idx,
    accommodation,
    reservationCount,
    carouselImages,
    ctaTitle,
    ctaUrl,
    ctaVariant,
    isLinkEnabled,
    shouldShowButton,
    shouldShowPrice,
    enableHoverShadow,
    enableHorizontalView,
    eventPhoto,
    eventDisplayName,
    onSelectAccommodation,
    setLocationBeingHovered,
  } = config;

  const { displayName, description, id, type, interestedGuestCount, distance, distances, starRating, pricePerNight, strikeoutPricePerNight, cutoffDate, latitude, longitude } =
    accommodation;
  const { locationInfo } = config;

  const venueLocation = getValidCoordinates(locationInfo);
  const hotelLocation = getValidCoordinates({ latitude, longitude }) ?? undefined;

  // Map pricing based on accommodation type
  let pricing: HotelTileCardProps['pricing'];
  if (shouldShowPrice) {
    if (type === AccommodationCombinedType.Roomblock) {
      // Room block pricing (Mode 3)
      pricing = {
        roomBlock: {
          pricePerNight: pricePerNight ?? 0,
          strikeoutPricePerNight: strikeoutPricePerNight ?? undefined,
          cutoffDate: cutoffDate ?? undefined,
        },
      };
    } else if (type === AccommodationCombinedType.Hotel) {
      // Hotel pricing - use internal fetch mode (Mode 2)
      pricing = {
        accommodationId: id,
      };
    }
  }

  // Map CTA - filter out 'link' variant as it's not supported in HotelTileCard
  const supportedVariants = ['solid', 'outline', 'ghost'] as const;
  const cta: HotelTileCardProps['cta'] = shouldShowButton
    ? {
        title: ctaTitle,
        url: ctaUrl,
        variant: supportedVariants.includes(ctaVariant as (typeof supportedVariants)[number]) ? (ctaVariant as (typeof supportedVariants)[number]) : 'solid',
        isEnabled: true,
        onClick: onSelectAccommodation,
      }
    : undefined;

  // Map tags
  const tags: HotelTileCardProps['tags'] = {
    reservationCount,
    interestedGuestCount: interestedGuestCount ?? undefined,
    showDiscountBadge: type === AccommodationCombinedType.Hotel,
    accommodationType: type,
  };

  // Map distance
  const distanceData: HotelTileCardProps['distance'] = distance || distances ? { milesToVenue: distance, distances } : undefined;

  return {
    displayName: displayName || '',
    description: description || undefined,
    starRating: starRating || undefined,
    photos: carouselImages || undefined,
    distance: distanceData,
    pricing,
    cta,
    tags,
    layout: {
      enableHorizontalView,
      enableHoverShadow,
      showIndex: idx + 1,
      hotelLocation,
      venueLocation,
    },
    eventContext: {
      eventDisplayName,
      eventPhoto: eventPhoto || undefined,
    },
    onMouseEnter: () => setLocationBeingHovered?.(id),
    onMouseLeave: () => setLocationBeingHovered?.(undefined),
    onClick: onSelectAccommodation,
    linkProps: isLinkEnabled ? { href: ctaUrl, target: '_blank' } : undefined,
    id,
  };
};
